"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
  AppWindow,
  Boxes,
  Cable,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Database,
  ExternalLink,
  Fingerprint,
  Gauge,
  HardDrive,
  KeyRound,
  LoaderCircle,
  Plus,
  RefreshCw,
  Search,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Workflow,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UniversalInfrastructureActions } from "./universal-infrastructure-actions";
import { NewInfrastructureLiveConsole, NewInfrastructureSetupSession, type NewInfrastructureLiveData, type NewInfrastructureLiveSetup } from "./new-infrastructure-live-console";
import { EnterpriseApprovalWorkspace } from "./enterprise-approval-workspace";
import { OwnerRootServiceCatalogue } from "./owner-root-service-catalogue";
import { useRunyiValueSync } from "../lib/runyi-value-sync";

export type UniversalCreationRoute =
  | "ai"
  | "applications"
  | "catalogue"
  | "bridge"
  | "flow"
  | "infrastructure";

type CreationMode = "infrastructure" | "marketplace" | "existing" | "blueprint" | "advanced" | "ai";

type Capability = {
  provider_capability_key: string;
  provider_key: string;
  display_name: string;
  service_type: string;
  logical_capability_hint?: string | null;
  state: string;
  risk_level?: string | null;
  approval_required?: boolean;
  owner_only?: boolean;
  supported_environments?: string[];
  category_key: string;
  requirement_profile?: RequirementProfile | null;
  execution?: {
    profile_key?: string | null;
    coverage_state?: string | null;
    blueprint_key?: string | null;
    action_key?: string | null;
  } | null;
  provider_connections?: Array<{
    connection_key: string;
    display_name: string;
    auth_mode: string;
    health_state: string;
    last_verified_at?: string | null;
  }>;
};

type RequirementProfile = {
  profile_key: string;
  provider_capability_key: string;
  provider_key: string;
  logical_capability: string;
  resource_type: string;
  auth_methods?: string[];
  required_connection_capabilities?: string[];
  credential_schema?: {
    fields?: CredentialField[];
  };
  configuration_schema?: {
    fields?: ConfigurationField[];
  };
};

type CredentialField = {
  key: string;
  label: string;
  type: "text" | "secret" | "secret_textarea";
  required?: boolean;
};

type ConfigurationField = {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  default?: string | number | boolean | null;
};

type Catalogue = {
  architecture_version: string;
  capabilities: Capability[];
  providers: Array<Record<string, unknown>>;
  applications: Array<{
    entity_key: string;
    display_name: string;
    environment_key?: string | null;
    lifecycle_state?: string;
    health_state?: string;
  }>;
  services?: Array<{ service_key:string; display_name:string; domain:string; lifecycle_state:string; identity_email?:string|null; capability_keys?:string[]; action_keys?:string[]; }>;
  backend_services?: Array<{ backend_service_key:string; display_name:string; logical_role:string; provider_key?:string|null; lifecycle_state:string; health_state:string; identity_email?:string|null }>;
  resources?: Array<{ entity_key:string; display_name:string; entity_type_key:string; domain?:string|null; lifecycle_state:string; health_state:string; identity_email?:string|null; tenant_project_ref?:string|null; }>;
  resource_total_count?: number;
  resource_next_offset?: number | null;
  projects: Array<{
    project_id: string;
    project_key: string;
    display_name: string;
    environment_key: string;
    lifecycle_state: string;
    assignment_state: string;
    application_entity_key?: string | null;
  }>;
};

type AddSession = {
  session_id: string;
  mode: string;
  application_entity_key?: string | null;
  project_id?: string | null;
  environment_key: string;
  assignment_role: string;
  category_key?: string | null;
  provider_capability_key?: string | null;
  provider_key?: string | null;
  target_entity_key?: string | null;
  current_step: string;
  status: string;
  reuse_policy: string;
  configuration?: Record<string, unknown>;
  connection_refs?: string[];
  credential_refs?: string[];
  result?: Record<string, any>;
};

type Requirement = {
  requirement_key: string;
  requirement_type: string;
  label: string;
  state: "SATISFIED" | "ACTION_REQUIRED" | "OPTIONAL" | "BLOCKED" | "NOT_APPLICABLE" | "VERIFYING";
  required: boolean;
  action_kind?: string | null;
  action_payload?: Record<string, any>;
  satisfied_reference?: string | null;
  verification?: Record<string, any>;
};

type AddInfrastructureIntent = {
  source?: string;
  provider_capability_key?: string | null;
  provider_key?: string | null;
  category_key?: string | null;
  target_entity_key?: string | null;
  logical_capability?: string | null;
  environment_key?: string | null;
  application_entity_key?: string | null;
  project_id?: string | null;
  mode?: "NEW" | "EXISTING";
  target_scope?: "APPLICATION" | "PROJECT" | null;
};

type ResolvedSession = {
  session: AddSession;
  capability?: Capability;
  profile?: RequirementProfile;
  requirements: Requirement[];
  reuse_candidates: Array<{
    resource_key: string;
    resource_type: string;
    display_name: string;
    provider_key?: string | null;
    environment_key?: string | null;
    region_ref?: string | null;
    lifecycle_state: string;
    health_state: string;
  }>;
  preview?: Record<string, any>;
  inputs?: Record<string, any>;
};

const creationModes: Array<{ id: CreationMode; label: string; description: string; icon: LucideIcon }> = [
  { id: "infrastructure", label: "NEW INFRASTRUCTURE", description: "Complete backend-requested provider authentication, credentials and permissions.", icon: Plus },
  { id: "marketplace", label: "MARKETPLACE", description: "Browse registered infrastructure and add it to an App or Project.", icon: Database },
  { id: "existing", label: "ADD EXISTING", description: "Discover and register provider resources that already exist.", icon: Cable },
  { id: "ai", label: "AI ARCHITECT", description: "Design infrastructure through RUNYI AI.", icon: Sparkles },
  { id: "blueprint", label: "BLUEPRINTS", description: "Reusable enterprise infrastructure stacks.", icon: Boxes },
  { id: "advanced", label: "ADVANCED", description: "Factory, manifests and expert controls.", icon: ServerCog },
];

const wizardSteps = [
  { id: "WHAT", label: "What" },
  { id: "WHERE", label: "Where" },
  { id: "REUSE_OR_CREATE", label: "Reuse/Create" },
  { id: "ACCESS_REQUIREMENTS", label: "Access" },
  { id: "CONFIGURE", label: "Configure" },
  { id: "REVIEW", label: "Review" },
  { id: "PROVISION", label: "Provision" },
];

const categoryIcons: Record<string, LucideIcon> = {
  DATABASE: Database,
  VECTOR_DATABASE: Database,
  STORAGE: HardDrive,
  OBJECT_STORAGE: HardDrive,
  COMPUTE: ServerCog,
  EDGE_COMPUTE: ServerCog,
  SERVERLESS: ServerCog,
  AI: Sparkles,
  IDENTITY: ShieldCheck,
  SECURITY: ShieldCheck,
};

const statusTone: Record<string,string> = {
  SATISFIED: "border-emerald-500/20 bg-emerald-500/[0.06] text-emerald-300",
  ACTION_REQUIRED: "border-amber-500/20 bg-amber-500/[0.06] text-amber-200",
  BLOCKED: "border-red-500/20 bg-red-500/[0.06] text-red-200",
  OPTIONAL: "border-foreground/10 bg-foreground/[0.03] text-foreground/55",
  NOT_APPLICABLE: "border-foreground/10 bg-foreground/[0.03] text-foreground/45",
  VERIFYING: "border-primary/20 bg-primary/[0.06] text-primary",
};

function messageFrom(error: unknown) {
  if (error instanceof Error) return error.message;
  return String(error ?? "Unknown error");
}

async function functionInvokeError(error: unknown) {
  const value=error as {message?:string;context?:Response};
  let payload:Record<string,unknown>|null=null;
  try {
    const response=value?.context;
    if (response && typeof response.clone==="function") {
      payload=await response.clone().json() as Record<string,unknown>;
    }
  } catch {
    payload=null;
  }
  if (!payload) return new Error(value?.message || "RUNYI backend request failed.");

  const detail=String(payload.detail ?? payload.error ?? value?.message ?? "RUNYI backend request failed.");
  const code=String(payload.error_code ?? "").trim();
  const stage=String(payload.stage ?? "").trim();
  const correlation=String(payload.correlation_id ?? "").trim();
  const context=[
    code && code!==detail ? code : "",
    stage ? `stage ${stage}` : "",
    correlation ? `ref ${correlation.slice(0,8)}` : "",
  ].filter(Boolean).join(" · ");
  return new Error(context ? `${detail} · ${context}` : detail);
}

function pretty(value: string | null | undefined) {
  return String(value ?? "").replaceAll("_"," ").replace(/\b\w/g,(letter) => letter.toUpperCase());
}

function universalBrowserBrokerSupported() {
  if (typeof window === "undefined") return false;
  const nativeWindow = window as Window & {
    RunyiAndroid?: {
      getRuntimeInfo?: () => string;
    };
  };
  // Normal web clients do not need a native provider-host allowlist, so they
  // can use the universal broker immediately.
  if (!nativeWindow.RunyiAndroid) return true;
  try {
    const raw=nativeWindow.RunyiAndroid.getRuntimeInfo?.();
    if (!raw) return false;
    const info=JSON.parse(raw) as {shell_version_code?:number};
    return Number(info.shell_version_code ?? 0) >= 67;
  } catch {
    return false;
  }
}

function openProviderAuthorization(url:string) {
  if (typeof window === "undefined") return false;
  const nativeWindow = window as Window & {
    RunyiAndroid?: {
      openOneTimeOAuth?: (authorizationUrl:string) => boolean;
      openOneTimeInfrastructureBrowser?: (authorizationOrConfigurationUrl:string) => boolean;
    };
  };
  // Do not detach JavaScriptInterface methods from their Android bridge
  // receiver; some WebViews reject unbound native calls.
  if (nativeWindow.RunyiAndroid?.openOneTimeInfrastructureBrowser) {
    try {
      return nativeWindow.RunyiAndroid.openOneTimeInfrastructureBrowser(url) === true;
    } catch {
      return false;
    }
  }
  if (nativeWindow.RunyiAndroid?.openOneTimeOAuth) {
    try {
      return nativeWindow.RunyiAndroid.openOneTimeOAuth(url) === true;
    } catch {
      return false;
    }
  }
  // Web-only RUNYI can use the broker in the browser normally. The packaged
  // Android shell never downgrades one-time infrastructure auth to an
  // uncontrolled shared-cookie provider tab.
  window.location.assign(url);
  return true;
}

export function UniversalInfrastructureCreationPlane({
  onNavigate,
  onOpenAdvancedFactory,
  supabase,
  connected,
  role,
}: {
  onNavigate: (route: UniversalCreationRoute) => void;
  onOpenAdvancedFactory: () => void;
  supabase: SupabaseClient | null;
  connected: boolean;
  role?: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
}) {
  const [mode,setMode] = useState<CreationMode | null>(null);
  const [catalogue,setCatalogue] = useState<Catalogue | null>(null);
  const [resourcePageBusy,setResourcePageBusy] = useState(false);
  const [query,setQuery] = useState("");
  const [category,setCategory] = useState<string>("ALL");
  const [resolved,setResolved] = useState<ResolvedSession | null>(null);
  const [busy,setBusy] = useState(false);
  const [notice,setNotice] = useState<string | null>(null);
  const [configuration,setConfiguration] = useState<Record<string,string>>({});
  const [credentialOpen,setCredentialOpen] = useState(false);
  const [credentialValues,setCredentialValues] = useState<Record<string,string>>({});
  const [credentialRequirement,setCredentialRequirement] = useState<Requirement | null>(null);
  const [pendingIntent,setPendingIntent] = useState<AddInfrastructureIntent | null>(null);
  const [newInfrastructureLive,setNewInfrastructureLive] = useState<NewInfrastructureLiveData | null>(null);
  const [newInfrastructureRealtime,setNewInfrastructureRealtime] = useState(false);
  const attemptedFirstUseRecoveries=useRef<Set<string>>(new Set());
  const [targetPreference,setTargetPreference] = useState<"APPLICATION" | "PROJECT" | null>(null);

  async function invoke(body: Record<string,unknown>) {
    if (!supabase) throw new Error("RUNYI authentication is required.");
    const { data,error } = await supabase.functions.invoke("runyi-infrastructure-acquisition",{ body });
    if (error) throw await functionInvokeError(error);
    if (data?.error) {
      const detail=String(data.detail ?? data.error);
      const context=[
        data.error_code ? String(data.error_code) : "",
        data.stage ? `stage ${String(data.stage)}` : "",
        data.correlation_id ? `ref ${String(data.correlation_id).slice(0,8)}` : "",
      ].filter(Boolean).join(" · ");
      throw new Error(context ? `${detail} · ${context}` : detail);
    }
    return data;
  }

  async function loadCatalogue(silent = false) {
    if (!supabase || !connected) return;
    if (!silent) { setBusy(true); setNotice(null); }
    try {
      const data = await invoke({action:"catalogue"});
      setCatalogue(data as Catalogue);
    } catch (error) {
      setNotice(messageFrom(error));
      if (silent) throw error;
    } finally {
      if (!silent) setBusy(false);
    }
  }

  async function loadMoreOwnerRootResources() {
    const nextOffset = catalogue?.resource_next_offset;
    if (nextOffset == null || resourcePageBusy || !supabase || !connected || role !== "OWNER_ADMIN") return;
    setResourcePageBusy(true);
    try {
      const page = await invoke({action:"catalogue_resources",offset:nextOffset}) as {
        resources?: NonNullable<Catalogue["resources"]>;
        resource_total_count?: number;
        resource_next_offset?: number|null;
      };
      setCatalogue((current) => {
        if (!current) return current;
        const resources = new Map((current.resources ?? []).map((item) => [item.entity_key,item]));
        for (const item of page.resources ?? []) resources.set(item.entity_key,item);
        return {
          ...current,resources:[...resources.values()],
          resource_total_count:page.resource_total_count ?? current.resource_total_count,
          resource_next_offset:page.resource_next_offset ?? null,
        };
      });
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setResourcePageBusy(false);
    }
  }

  async function loadNewInfrastructureLive(silent = false) {
    if (!supabase || !connected) return;
    if (!silent) {
      setBusy(true);
      setNotice(null);
    }
    try {
      const data = await invoke({action:"new_infrastructure_live"});
      setNewInfrastructureLive(data as NewInfrastructureLiveData);
    } catch (error) {
      if (!silent) setNotice(messageFrom(error));
    } finally {
      if (!silent) setBusy(false);
    }
  }

  async function openNewInfrastructureSetup(setupId: string) {
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({action:"ensure_setup_session",setup_id:setupId});
      setResolved(data as ResolvedSession);
      setConfiguration({});
      setMode("infrastructure");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function continueNewInfrastructureSetup(setupId: string, verificationMode:"CODE"|"LINK"="LINK") {
    if (role==="MEMBER") {
      setNotice("Infrastructure setup requires an Owner/Admin management session. The current RUNYI session is MEMBER; sign back into the authorized management account before continuing.");
      return;
    }
    const liveSetup=(newInfrastructureLive?.setups ?? []).find((item)=>item.setup_id===setupId);
    const liveAction=String(liveSetup?.action_kind ?? liveSetup?.digital_display?.next_human_action?.kind ?? "").toUpperCase();
    if (["SELECT_PROJECT","CREATE_PROJECT"].includes(liveAction)) {
      await openNewInfrastructureSetup(setupId);
      return;
    }

    if (["MEMBER_TENANT_ENROLLMENT","VERIFY_MEMBER_CONFIRMATION_LINK","ENTER_MEMBER_OTP","CONFIGURE_MEMBER_EMAIL_CHANNEL"].includes(liveAction)) {
      setBusy(true);
      setNotice(null);
      try {
        const started=await invoke({action:"member_activation_begin_enrollment",setup_id:setupId,verification_mode:verificationMode});
        if (!["VERIFY_MEMBER_CONFIRMATION_LINK","ENTER_MEMBER_OTP"].includes(String(started?.next_action ?? ""))) {
          await loadNewInfrastructureLive(true);
          setNotice(String(started?.summary ?? (
            started?.next_action==="APPROVE_MEMBER_HEALTH_REPAIR"
              ? "Member infrastructure health needs an Owner-approved fixed repair before email verification. No email was sent."
              : "Member enrollment is recorded; RUNYI is refreshing the authoritative account status."
          )));
          if(started?.next_action==="APPROVE_MEMBER_HEALTH_REPAIR") {
            window.dispatchEvent(new Event("runyi:show-infrastructure-approvals"));
          }
          return;
        }
        resetSession();
        setMode("infrastructure");
        await loadNewInfrastructureLive(true);
        setNotice(verificationMode==="CODE"
          ? "Member-project numeric code sent. Enter the code in New Infrastructure to verify the member identity."
          : "Member-project confirmation email sent. Paste the link into the secure verification box in New Infrastructure.");
      } catch (error) {
        setNotice(messageFrom(error));
        await loadNewInfrastructureLive(true);
      } finally {
        setBusy(false);
      }
      return;
    }

    if (liveAction==="RECONCILE_MARKETPLACE") {
      setBusy(true);
      setNotice(null);
      try {
        const outcome=await invoke({action:"member_activation_reconcile_marketplace",setup_id:setupId});
        await loadNewInfrastructureLive(true);
        setNotice(outcome?.completed===true
          ? "Verified member infrastructure and component services activated in the RUNYI marketplace."
          : outcome?.next_action==="APPROVE_MEMBER_HEALTH_REPAIR"
          ? "Approve the scoped MEMBER health repair in New Infrastructure → Protected Approvals, then tap Finish Marketplace Activation again. Your confirmed email remains verified."
          : outcome?.next_action==="APPROVE_MEMBER_SESSION_RECOVERY"
          ? "Approve the scoped member-session recovery in New Infrastructure → Protected Approvals, then tap Finish Marketplace Activation again. No new email confirmation is needed."
          : String(outcome?.summary ?? "The member identity is verified; RUNYI still needs to complete marketplace reconciliation."));
      } catch(error) {
        setNotice(messageFrom(error));
        await loadNewInfrastructureLive(true);
      } finally {
        setBusy(false);
      }
      return;
    }

    if (liveAction==="PREPARE_BOOTSTRAP") {
      setBusy(true);
      setNotice(null);
      try {
        await invoke({action:"prepare_member_bootstrap",setup_id:setupId});
        await loadNewInfrastructureLive(true);
        setNotice("Canonical bootstrap preflight passed. Review and approve the Control Tower v4 bootstrap from this NEW INFRASTRUCTURE setup.");
      } catch (error) {
        setNotice(messageFrom(error));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (liveAction==="APPROVE_BOOTSTRAP") {
      setBusy(true);
      setNotice(null);
      try {
        await invoke({action:"approve_member_bootstrap",setup_id:setupId});
        await loadNewInfrastructureLive(true);
        setNotice("Owner-approved member bootstrap verified. Continue enrollment for the selected member account.");
      } catch (error) {
        setNotice(messageFrom(error));
        await loadNewInfrastructureLive(true);
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({
        action:"begin_setup_authorization",
        setup_id:setupId,
        browser_broker_supported:universalBrowserBrokerSupported(),
      }) as ResolvedSession & {
        authorization_url?:string;
        authorization_required?:boolean;
        next_action?:string;
      };
      setResolved(data);
      setConfiguration({});
      setMode("infrastructure");

      if (data.authorization_url) {
        if (!openProviderAuthorization(String(data.authorization_url))) {
          setNotice("RUNYI blocked a shared-cookie browser fallback. Update the RUNYI Android app and browser to versions that support isolated Ephemeral Custom Tabs, then try Continue in Browser again.");
        }
        return;
      }

      const requirement=(data.requirements ?? []).find((item) => item.requirement_key==="provider_connection" && item.state!=="SATISFIED");
      if (!requirement) {
        await loadNewInfrastructureLive(true);
        return;
      }

      const action=String(data.next_action ?? requirement.action_kind ?? "").toUpperCase();
      if (["ADD_API_TOKEN","ADD_SERVICE_ACCOUNT","ADD_GITHUB_APP","ADD_TOKEN","ADD_CLIENT_CREDENTIAL","CONNECT_MCP","CONNECT_PROVIDER"].includes(action)) {
        setCredentialRequirement(requirement);
        setCredentialValues({});
        setCredentialOpen(true);
        return;
      }

      if (action==="VERIFY") {
        const verified=await invoke({action:"verify_access",session_id:data.session.session_id});
        setResolved(verified as ResolvedSession);
        await loadNewInfrastructureLive(true);
      }
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function selectNewInfrastructureProject(setupId:string, projectRef:string) {
    setBusy(true);
    setNotice(null);
    try {
      const data=await invoke({
        action:"select_provider_project",
        setup_id:setupId,
        project_ref:projectRef,
      });
      setResolved(data as ResolvedSession);
      await loadNewInfrastructureLive(true);
      setNotice("Supabase project selected. RUNYI is continuing project registration and verification.");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function createNewInfrastructureProject(setupId:string, details:{organization_slug:string;project_name:string;region_group:"americas"|"emea"|"apac"}) {
    if (!setupId || !details.organization_slug) return;
    const matching=(newInfrastructureLive?.setups ?? []).find((item)=>item.setup_id===setupId);
    if (!matching || String(matching.action_kind).toUpperCase()!=="CREATE_PROJECT"
      || !(matching.organization_options ?? []).some((item)=>item.slug===details.organization_slug)) {
      setNotice("The verified provider organization or project action changed. Refresh MEMBER setup before creating infrastructure.");
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const data=await invoke({
        action:"create_provider_project",
        setup_id:setupId,
        organization_slug:details.organization_slug,
        project_name:details.project_name,
        region_group:details.region_group,
      });
      setResolved(data as ResolvedSession);
      await loadNewInfrastructureLive(true);
      setNotice("Free-only project creation requested through RUNYI governance. RUNYI will independently verify the resulting provider project before registering MEMBER infrastructure.");
    } catch(error) {
      setNotice(messageFrom(error));
      await loadNewInfrastructureLive(true);
    } finally {
      setBusy(false);
    }
  }

  async function restoreSession(sessionId: string) {
    if (!sessionId) return;
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({action:"session_status",session_id:sessionId});
      setResolved(data as ResolvedSession);
      setConfiguration(Object.fromEntries(Object.entries(data.session?.configuration ?? {}).map(([key,value]) => [key,String(value ?? "")])));
      const restoredMode=String(data.session?.mode ?? "NEW").toUpperCase();
      setMode(restoredMode === "SETUP" ? "infrastructure" : restoredMode === "EXISTING" ? "existing" : "marketplace");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  const catalogueSync = useRunyiValueSync({
    supabase, connected, domains: ["catalogue", "infrastructure", "applications", "projects", "ai", "identity"],
    onRefresh: () => loadCatalogue(true), intervalMs: 60_000,
  });

  useEffect(() => {
    if (mode !== "infrastructure" || !supabase || !connected) return;

    void loadNewInfrastructureLive();
    const interval=window.setInterval(() => void loadNewInfrastructureLive(true),12000);
    // Returning from external login/MFA may resume the existing WebView rather
    // than reload it. Reconcile authoritative setup state immediately; never
    // infer a successful OAuth grant merely from foregrounding RUNYI.
    const refreshOnReturn = () => {
      if (document.visibilityState === "visible") void loadNewInfrastructureLive(true);
    };
    window.addEventListener("focus",refreshOnReturn);
    document.addEventListener("visibilitychange",refreshOnReturn);

    const channel=supabase
      .channel("runyi-new-infrastructure-live")
      .on("postgres_changes",{event:"*",schema:"public",table:"runyi_new_infrastructure_setups"},() => {
        setNewInfrastructureRealtime(true);
        void loadNewInfrastructureLive(true);
      })
      .subscribe((status) => {
        setNewInfrastructureRealtime(status === "SUBSCRIBED");
      });

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus",refreshOnReturn);
      document.removeEventListener("visibilitychange",refreshOnReturn);
      setNewInfrastructureRealtime(false);
      void supabase.removeChannel(channel);
    };
  // The live screen reconciles through Edge; realtime is only a non-secret refresh signal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[mode,connected,supabase]);
  // One bounded, Owner-authenticated continuation after an exact repair was
  // approved and backend state reconciled. No new OTP/link, no approval bypass,
  // and no repeating calls on every realtime/poll refresh.
  useEffect(() => {
    if(mode!=="infrastructure" || role!=="OWNER_ADMIN" || !connected || !supabase || busy) return;
    const ready=(newInfrastructureLive?.setups ?? []).find((item) =>
      String(item.action_kind)==="RECONCILE_MARKETPLACE"
      && Number(item.progress_percent)<100
      && item.metadata?.health_repair_state==="COMPLETED_PENDING_FIRST_USE"
      && item.metadata?.member_activation_state==="MEMBER_IDENTITY_VERIFIED_HEALTH_REPAIR_PENDING"
      && typeof item.metadata?.health_repair_approval_id==="string"
    );
    if(!ready) return;
    const key=String(ready.setup_id)+":"+String(ready.metadata?.health_repair_approval_id);
    if(attemptedFirstUseRecoveries.current.has(key)) return;
    attemptedFirstUseRecoveries.current.add(key);
    void continueNewInfrastructureSetup(ready.setup_id);
  // The Edge response is the authority; after one failed attempt the Owner can
  // explicitly retry. No effects may mint approval or simulate validation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[mode,role,connected,supabase,busy,newInfrastructureLive]);




  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleBack=(event:Event) => {
      if (!(event instanceof CustomEvent)) return;
      if (credentialOpen) {
        event.preventDefault();
        setCredentialValues({});
        setCredentialRequirement(null);
        setCredentialOpen(false);
        return;
      }
      const currentSession=resolved?.session ?? null;
      if (currentSession && String(currentSession.mode).toUpperCase()==="SETUP") {
        event.preventDefault();
        resetSession();
        setMode("infrastructure");
        void loadNewInfrastructureLive(true);
        return;
      }
      if (currentSession) {
        event.preventDefault();
        if (busy) return; // Never interrupt an in-flight governed backend action.
        const currentStep = String(currentSession.current_step ?? "").toUpperCase();
        if (currentStep === "REVIEW") {
          void patchSession({ current_step: "CONFIGURE" });
          return;
        }
        if (currentStep === "CONFIGURE" || currentStep === "ACCESS_REQUIREMENTS") {
          void patchSession({ configuration: mergedConfiguration({ acquisition_choice: "" }) });
          return;
        }
        resetSession();
        return;
      }
      if (mode !== null) {
        event.preventDefault();
        returnToLauncher();
      }
    };
    window.addEventListener("runyi:request-back",handleBack as EventListener);
    return () => window.removeEventListener("runyi:request-back",handleBack as EventListener);
  // Back is intentionally hierarchical inside Create before the enterprise route stack runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[resolved,mode,credentialOpen,configuration,busy]);

  useEffect(() => {
    if (typeof window === "undefined" || !catalogue || session) return;
    let intent: AddInfrastructureIntent | null = null;
    try {
      const raw = window.sessionStorage.getItem("runyi:add-infrastructure:intent");
      if (raw) {
        intent = JSON.parse(raw) as AddInfrastructureIntent;
        window.sessionStorage.removeItem("runyi:add-infrastructure:intent");
      }
    } catch {
      intent = null;
    }
    if (!intent) return;

    setPendingIntent(intent);
    setTargetPreference(intent.target_scope ?? null);
    setMode(intent.mode === "EXISTING" ? "existing" : "marketplace");

    const exact = intent.provider_capability_key
      ? catalogue.capabilities.find((item) => item.provider_capability_key === intent?.provider_capability_key)
      : null;
    if (exact) {
      void startCapability(exact,intent);
      return;
    }

    const matches = catalogue.capabilities.filter((item) => {
      if (intent?.provider_key && item.provider_key !== intent.provider_key) return false;
      if (intent?.category_key && item.category_key !== intent.category_key) return false;
      if (intent?.logical_capability && item.logical_capability_hint !== intent.logical_capability) return false;
      return true;
    });
    if (matches.length === 1) {
      void startCapability(matches[0],intent);
      return;
    }
    if (intent.provider_key) setQuery(intent.provider_key);
    if (intent.category_key && categories.some(([key]) => key === intent?.category_key)) {
      setCategory(intent.category_key);
    }
  // startCapability is intentionally invoked only after the loaded catalogue establishes the dynamic capability set.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[catalogue]);

  useEffect(() => {
    if (typeof window === "undefined" || !catalogue) return;
    const handleAddInfrastructure = (event: Event) => {
      const intent = (event as CustomEvent<AddInfrastructureIntent>).detail ?? {};
      setPendingIntent(intent);
      setTargetPreference(intent.target_scope ?? null);
      setMode(intent.mode === "EXISTING" ? "existing" : "marketplace");
      const exact = intent.provider_capability_key
        ? catalogue.capabilities.find((item) => item.provider_capability_key === intent.provider_capability_key)
        : null;
      if (exact) {
        void startCapability(exact,intent);
        return;
      }
      if (intent.provider_key) setQuery(intent.provider_key);
      if (intent.category_key) setCategory(intent.category_key);
    };
    window.addEventListener("runyi:add-infrastructure",handleAddInfrastructure as EventListener);
    return () => window.removeEventListener("runyi:add-infrastructure",handleAddInfrastructure as EventListener);
  // startCapability resolves against explicit intent payload; catalogue is the discovery authority.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[catalogue]);

  useEffect(() => {
    if (typeof window === "undefined" || !supabase || !connected) return;
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("runyi_infrastructure_session");
    const status = url.searchParams.get("runyi_infrastructure_status");
    const message = url.searchParams.get("runyi_infrastructure_message");
    if (!sessionId) return;
    if (message) setNotice(status === "error" ? `Authorization failed: ${message}` : message);
    void restoreSession(sessionId);
    url.searchParams.delete("runyi_infrastructure_session");
    url.searchParams.delete("runyi_infrastructure_status");
    url.searchParams.delete("runyi_infrastructure_message");
    window.history.replaceState(window.history.state,"",url.toString());
  },[connected,supabase]);

  const capabilities = useMemo(() => {
    const items = catalogue?.capabilities ?? [];
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (category !== "ALL" && item.category_key !== category) return false;
      if (!needle) return true;
      return [
        item.display_name,item.provider_key,item.service_type,item.logical_capability_hint,
        item.category_key,item.execution?.coverage_state,
      ].join(" ").toLowerCase().includes(needle);
    });
  },[catalogue,query,category]);

  const categories = useMemo(() => {
    const counts = new Map<string,number>();
    for (const item of catalogue?.capabilities ?? []) counts.set(item.category_key,(counts.get(item.category_key) ?? 0)+1);
    return [...counts.entries()].sort((a,b) => a[0].localeCompare(b[0]));
  },[catalogue]);

  const session = resolved?.session ?? null;
  const activeCapability = resolved?.capability
    ?? catalogue?.capabilities.find((item) => item.provider_capability_key === session?.provider_capability_key)
    ?? null;
  const activeProfile = resolved?.profile ?? activeCapability?.requirement_profile ?? null;

  const step = useMemo(() => {
    if (!session) return "WHAT";
    if (String(session.mode).toUpperCase() === "SETUP") return "ACCESS_REQUIREMENTS";
    if (session.status === "ACTIVE") return "PROVISION";
    if (["PROVISIONING","VERIFYING","REGISTERING","DEGRADED"].includes(session.status)) return "PROVISION";
    if (session.status === "PLAN_READY" || session.current_step === "REVIEW") return "REVIEW";
    if (!session.application_entity_key && !session.project_id) return "WHERE";
    if (!String((session.configuration ?? {}).acquisition_choice ?? "")) return "REUSE_OR_CREATE";
    const blocked = (resolved?.requirements ?? []).some((item) => item.required && item.state !== "SATISFIED");
    if (blocked) return "ACCESS_REQUIREMENTS";
    if (session.current_step === "REVIEW") return "REVIEW";
    return "CONFIGURE";
  },[session,resolved]);

  const stepIndex = Math.max(0,wizardSteps.findIndex((item) => item.id === step));

  async function startCapability(item: Capability, intentOverride?: AddInfrastructureIntent | null) {
    const intent = intentOverride ?? pendingIntent;
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({
        action:"start_session",
        mode:intent?.mode ?? (mode === "existing" ? "EXISTING" : "NEW"),
        provider_capability_key:item.provider_capability_key,
        provider_key:intent?.provider_key ?? item.provider_key,
        category_key:intent?.category_key ?? item.category_key,
        application_entity_key:intent?.application_entity_key ?? null,
        project_id:intent?.project_id ?? null,
        target_entity_key:intent?.target_entity_key ?? null,
        environment_key:intent?.environment_key ?? "PRODUCTION",
        assignment_role:"PRIMARY",
        return_route:"new",
      });
      setResolved(data as ResolvedSession);
      setConfiguration(Object.fromEntries(Object.entries(data.session?.configuration ?? {}).map(([key,value]) => [key,String(value ?? "")])));
      setPendingIntent(null);
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function patchSession(patch: Record<string,unknown>) {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({action:"update_session",session_id:session.session_id,...patch});
      setResolved(data as ResolvedSession);
      if (data.session?.configuration) {
        setConfiguration(Object.fromEntries(Object.entries(data.session.configuration).map(([key,value]) => [key,String(value ?? "")])));
      }
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  function mergedConfiguration(extra: Record<string,unknown>) {
    return {...(session?.configuration ?? {}),...configuration,...extra};
  }

  async function chooseTarget(kind: "application" | "project", id: string, environment?: string | null) {
    await patchSession({
      application_entity_key:kind === "application" ? id : null,
      project_id:kind === "project" ? id : null,
      environment_key:environment || "PRODUCTION",
    });
  }

  async function chooseReuse(resourceKey?: string) {
    await patchSession({
      target_entity_key:resourceKey || null,
      reuse_policy:resourceKey ? "OWNER_CHOICE" : "CREATE_DEDICATED",
      configuration:mergedConfiguration({acquisition_choice:resourceKey ? "REUSE" : "CREATE"}),
    });
  }

  async function requirementAction(requirement: Requirement) {
    if (!session) return;
    const action = String(requirement.action_kind ?? "");
    if (action === "SELECT_TARGET") return;
    if (["PREPARE_BOOTSTRAP","APPROVE_BOOTSTRAP"].includes(action)) {
      const setupId=String(session.configuration?.new_infrastructure_setup_id ?? "");
      if (!setupId) {
        setNotice("The NEW INFRASTRUCTURE setup binding is unavailable. Refresh the Digital Display and try again.");
        return;
      }
      await continueNewInfrastructureSetup(setupId);
      return;
    }
    if (action === "BROWSER_AUTHORIZATION") {
      setBusy(true);
      setNotice(null);
      try {
        const data = await invoke({
          action:"start_authorization",
          session_id:session.session_id,
          browser_broker_supported:universalBrowserBrokerSupported(),
        });
        if (!data.authorization_url) throw new Error("Provider authorization URL was not returned.");
        if (!openProviderAuthorization(String(data.authorization_url))) {
          setNotice("RUNYI blocked a shared-cookie browser fallback. Update the RUNYI Android app and browser to versions that support isolated Ephemeral Custom Tabs, then try again.");
          setBusy(false);
        }
      } catch (error) {
        setNotice(messageFrom(error));
        setBusy(false);
      }
      return;
    }
    if (["ADD_API_TOKEN","ADD_SERVICE_ACCOUNT","ADD_GITHUB_APP","ADD_TOKEN","ADD_CLIENT_CREDENTIAL","CONNECT_MCP","CONNECT_PROVIDER"].includes(action)) {
      setCredentialRequirement(requirement);
      setCredentialValues({});
      setCredentialOpen(true);
      return;
    }
    if (action === "VERIFY") {
      await verifyAccess();
      return;
    }
    setNotice("This provider requirement is not executable yet. RUNYI kept it blocked instead of pretending support exists.");
  }

  async function verifyAccess() {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({action:"verify_access",session_id:session.session_id});
      setResolved(data as ResolvedSession);
      await loadNewInfrastructureLive(true);
      setNotice("Provider access verification completed.");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function installCredential() {
    if (!session || !credentialRequirement) return;
    const schema = credentialRequirement.action_payload?.credential_schema?.fields ?? activeProfile?.credential_schema?.fields ?? [];
    const missing = schema.filter((field: CredentialField) => field.required && !String(credentialValues[field.key] ?? "").trim());
    if (missing.length) {
      setNotice(`Required: ${missing.map((field: CredentialField) => field.label).join(", ")}`);
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({
        action:"install_credential",
        session_id:session.session_id,
        credentials:credentialValues,
        reason:String(session.mode).toUpperCase()==="SETUP"
          ? "Owner supplied backend-requested NEW INFRASTRUCTURE credential through RUNYI."
          : "Owner supplied provider credential through the RUNYI Add Infrastructure secure credential step.",
      });
      setCredentialValues({});
      setCredentialRequirement(null);
      setCredentialOpen(false);
      setResolved(data as ResolvedSession);
      await loadNewInfrastructureLive(true);
      setNotice("Credential submitted securely. Secret values were not returned to the app.");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function reviewPlan() {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    try {
      await invoke({
        action:"update_session",
        session_id:session.session_id,
        configuration:mergedConfiguration({}),
        current_step:"CONFIGURE",
      });
      const data = session.application_entity_key
        ? await invoke({action:"preview_plan",session_id:session.session_id})
        : await invoke({action:"save_project_plan",session_id:session.session_id});
      setResolved((current) => ({
        ...(current ?? {requirements:[],reuse_candidates:[]}),
        ...data,
        session:data.session,
        preview:data.preview,
        inputs:data.inputs,
      }) as ResolvedSession);
      setNotice(session.application_entity_key
        ? "Infrastructure plan is ready for Owner review."
        : "Infrastructure Project plan saved without provider mutation.");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function saveProjectPlan() {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({action:"save_project_plan",session_id:session.session_id});
      setResolved((current) => ({...(current ?? {requirements:[],reuse_candidates:[]}),...data,session:data.session}) as ResolvedSession);
      setNotice("Infrastructure Project revision saved. Provider infrastructure was not changed.");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function approveAndProvision() {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    try {
      const data = await invoke({action:"approve_and_handoff",session_id:session.session_id});
      setResolved((current) => ({...(current ?? {requirements:[],reuse_candidates:[]}),...data,session:data.session}) as ResolvedSession);
      setNotice("Owner approval recorded. The composition is materialized and unresolved provider work has entered governed Flow.");
    } catch (error) {
      setNotice(messageFrom(error));
    } finally {
      setBusy(false);
    }
  }

  async function refreshSession() {
    if (!session) return;
    await restoreSession(session.session_id);
  }

  function resetSession() {
    setResolved(null);
    setConfiguration({});
    setCredentialValues({});
    setCredentialRequirement(null);
    setCredentialOpen(false);
    setPendingIntent(null);
    setTargetPreference(null);
    setNotice(null);
    setQuery("");
  }

  function returnToLauncher() {
    resetSession();
    setMode(null);
    setCategory("ALL");
  }

  const chooseMode = (next: CreationMode) => {
    if (next === "advanced") return onOpenAdvancedFactory();
    if (next === "ai") return onNavigate("ai");
    if (next === "blueprint") return onOpenAdvancedFactory();
    resetSession();
    setMode(next);
    setCategory("ALL");
  };

  if (!connected || !supabase) {
    return (
      <div className="min-h-full bg-background px-4 py-6 text-foreground">
        <Card className="mx-auto max-w-xl border-foreground/10 bg-card py-0">
          <CardContent className="p-6 text-center">
            <ShieldCheck className="mx-auto size-8 text-primary" />
            <h1 className="mt-3 text-xl font-semibold">Sign in to add infrastructure</h1>
            <p className="mt-2 text-sm leading-6 text-foreground/45">The universal Add Infrastructure interface requires an authenticated RUNYI Owner/Admin session. Provider credentials are never exposed to this page after installation.</p>
            <Button className="mt-5 rounded-xl" onClick={() => onNavigate("infrastructure")}>Open Infrastructure</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background text-foreground">
      <div className={`mx-auto max-w-6xl px-3 pb-10 sm:px-6 ${mode === "infrastructure" && !session ? "space-y-2 py-2 sm:py-4" : "space-y-4 py-4 sm:py-6"}`}>
        <section className={mode === "infrastructure" && !session ? "border-0 bg-transparent shadow-none" : "rounded-[24px] border border-foreground/8 bg-card shadow-xl shadow-black/10"}>
          {!(mode === "infrastructure" && !session) && (
            <div className={`border-b border-foreground/8 ${mode === "infrastructure" && !session ? "p-3 sm:p-4" : "p-4 sm:p-5"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary">
                    {mode === "infrastructure" && !session ? "RUNYI · UNIVERSAL MARKETPLACE · DIGITAL DISPLAY" : "RUNYI · UNIVERSAL MARKETPLACE · Single Universal Interface"}
                  </p>
                  <h1 className={`mt-1 font-semibold tracking-tight ${mode === "infrastructure" && !session ? "text-lg sm:text-2xl" : "text-xl sm:text-3xl"}`}>
                    {mode === "infrastructure" && !session ? "NEW INFRASTRUCTURE" : "Infrastructure"}
                  </h1>
                  {!(mode === "infrastructure" && !session) && (
                    <p className="mt-1 max-w-3xl text-xs leading-5 text-foreground/45">
                      NEW INFRASTRUCTURE is the universal marketplace acquisition surface. It opens the Digital Display for infrastructure prepared by ChatGPT/coding agents and the backend, reuses or adopts existing resources before duplicate creation, and opens authentication only when human action is required. ADD INFRASTRUCTURE assigns verified registered infrastructure to an App or Infrastructure Project.
                    </p>
                  )}
                  {mode === "infrastructure" && !session && (
                    <p className="mt-0.5 text-[10px] text-foreground/38">Live backend status · provider truth · human actions</p>
                  )}
                </div>
                {session ? (
                  <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-xl" onClick={returnToLauncher}><X className="size-4" /><span className="hidden sm:inline">Close</span></Button>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-xl" onClick={onOpenAdvancedFactory}><ServerCog className="size-4" /><span className="hidden sm:inline">Advanced</span></Button>
                )}
              </div>
  
              {!session && mode === null && (
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-5">
                  {creationModes.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => chooseMode(item.id)}
                      className="flex min-h-[88px] w-full touch-manipulation items-start gap-3 rounded-2xl border border-foreground/8 bg-foreground/[0.02] p-3.5 text-left transition active:scale-[0.99] hover:border-primary/25 hover:bg-primary/[0.045]"
                      aria-label={item.label}
                    >
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-foreground/[0.05] text-primary"><item.icon className="size-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12px] font-semibold">{item.label}</span>
                        <span className="mt-1 block text-[10px] leading-4 text-foreground/42">{item.description}</span>
                      </span>
                      <ChevronRight className="mt-1 size-4 shrink-0 text-foreground/22" />
                    </button>
                  ))}
                </div>
              )}
  
              {!session && mode !== null && mode !== "infrastructure" && (
                <div className="mt-4 flex items-center justify-between gap-3">
                  <Button type="button" variant="ghost" size="sm" className="rounded-xl" onClick={returnToLauncher}>
                    <ChevronLeft className="size-4" />
                    Back
                  </Button>
                  <div className="min-w-0 flex-1 text-right">
                    <p className="truncate text-[10px] font-semibold text-foreground/70">{creationModes.find((item) => item.id === mode)?.label}</p>
                    <p className="truncate text-[9px] text-foreground/35">{mode === "marketplace" ? "Use registered infrastructure · ⋮ Add to App / Add to Project" : "Choose a capability to continue"}</p>
                  </div>
                </div>
              )}
  
              {session && String(session.mode).toUpperCase() !== "SETUP" && (
                <div className="mt-4 flex gap-1 overflow-x-auto pb-1">
                  {wizardSteps.map((item,index) => (
                    <div key={item.id} className="flex min-w-max items-center gap-1">
                      <span className={`rounded-lg border px-2.5 py-1.5 text-[9px] font-semibold ${index <= stepIndex ? "border-primary/25 bg-primary/[0.08] text-primary" : "border-foreground/8 bg-foreground/[0.02] text-foreground/35"}`}>{index + 1}. {item.label}</span>
                      {index < wizardSteps.length - 1 && <ChevronRight className="size-3 text-foreground/18" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={mode === "infrastructure" && !session ? "p-0" : "p-4 sm:p-5"}>
            {notice && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-foreground/10 bg-foreground/[0.025] p-3">
                <CircleAlert className="mt-0.5 size-4 shrink-0 text-primary" />
                <p className="min-w-0 flex-1 text-[11px] leading-5 text-foreground/65">{notice}</p>
                <button type="button" onClick={() => setNotice(null)}><X className="size-3.5 text-foreground/35" /></button>
              </div>
            )}

            {!session && mode === null && connected && role !== "MEMBER" && (
              <div className="mb-3">
                <EnterpriseApprovalWorkspace supabase={supabase} connected={connected} scope="infrastructure" />
              </div>
            )}

            {!session && mode === null && (
              <div className="rounded-2xl border border-dashed border-foreground/10 bg-foreground/[0.015] p-5 text-center">
                <p className="text-sm font-semibold">Choose an infrastructure operation</p>
                <p className="mt-1 text-[10px] leading-5 text-foreground/40">NEW INFRASTRUCTURE receives backend setup status and asks only for human authentication, credentials, consent or permissions. MARKETPLACE and ⋮ actions ADD registered infrastructure to an App or Project. ADD EXISTING adopts provider resources already created outside RUNYI.</p>
              </div>
            )}

            {mode === "infrastructure" && connected && role !== "MEMBER" && (
              <div className="mb-3 space-y-2">
                <EnterpriseApprovalWorkspace supabase={supabase} connected={connected} scope="infrastructure" />
                <OwnerRootServiceCatalogue catalogue={catalogue} loadingMore={resourcePageBusy}
                  syncState={catalogueSync.state} lastSyncedAt={catalogueSync.lastSyncedAt}
                  onLoadMore={() => void loadMoreOwnerRootResources()}
                  onRefresh={() => void loadCatalogue()} onOpenMarketplace={(value) => {
                  resetSession();
                  setMode("marketplace");
                  setQuery(value);
                  setCategory("ALL");
                }} />
              </div>
            )}

            {!session && mode === "infrastructure" && (
              <NewInfrastructureLiveConsole
                data={newInfrastructureLive}
                busy={busy}
                liveConnected={newInfrastructureRealtime}
                role={role}
                supabase={supabase}
                connected={connected}
                applications={(catalogue?.applications ?? []).map((app) => ({entity_key:app.entity_key,display_name:app.display_name}))}
                onOpenSetup={(key) => void openNewInfrastructureSetup(key)}
                onContinueSetup={(key) => void continueNewInfrastructureSetup(key)}
                onSendVerification={(key,method) => void continueNewInfrastructureSetup(key,method)}
                onRefresh={() => void loadNewInfrastructureLive()}
                onAddMember={() => {
                  resetSession();
                  setMode(null);
                  setNotice("Choose New Infrastructure to register a member. RUNYI checks existing projects before provisioning another.");
                }}
                onRegisterExisting={() => {
                  resetSession();
                  setMode("existing");
                  setQuery("");
                  setCategory("ALL");
                }}
                onMarketplace={() => {
                  setMode("marketplace");
                  setQuery("");
                  setCategory("ALL");
                }}
              />
            )}

            {!session && mode !== null && mode !== "infrastructure" && (
              <CatalogueChooser
                catalogue={catalogue}
                capabilities={capabilities}
                categories={categories}
                query={query}
                category={category}
                busy={busy}
                mode={mode}
                supabase={supabase}
                connected={connected}
                onQuery={setQuery}
                onCategory={setCategory}
                onSelect={startCapability}
                onRefresh={loadCatalogue}
              />
            )}

            {session && String(session.mode).toUpperCase() === "SETUP" && (
              <NewInfrastructureSetupSession
                setup={(newInfrastructureLive?.setups ?? []).find((item) =>
                  item.setup_session_id === session.session_id
                  || item.setup_id === String(session.configuration?.new_infrastructure_setup_id ?? "")
                ) ?? null}
                requirements={(resolved?.requirements ?? [])}
                busy={busy}
                credentialOpen={credentialOpen}
                credentialFields={(credentialRequirement?.action_payload?.credential_schema?.fields ?? activeProfile?.credential_schema?.fields ?? []) as CredentialField[]}
                credentialValues={credentialValues}
                onCredentialChange={(key,value) => setCredentialValues((current) => ({...current,[key]:value}))}
                onCredentialSubmit={() => void installCredential()}
                onCredentialClose={() => {setCredentialValues({});setCredentialRequirement(null);setCredentialOpen(false);}}
                onRequirementAction={(requirement) => void requirementAction(requirement as Requirement)}
                onContinueSetup={(key) => void continueNewInfrastructureSetup(key)}
                onSendVerification={(key,method) => void continueNewInfrastructureSetup(key,method)}
                onSelectProject={(projectRef) => {
                  const setupId=String(session.configuration?.new_infrastructure_setup_id ?? "");
                  if (setupId) void selectNewInfrastructureProject(setupId,projectRef);
                }}
                onCreateProject={(setupId,details) => void createNewInfrastructureProject(setupId,details)}
                onRetryProjectRegistration={(setupId,projectRef) => {
                  const latest=(newInfrastructureLive?.setups ?? []).find((item)=>item.setup_id===setupId);
                  if (!latest || latest.provider_key.toUpperCase()!=="SUPABASE"
                    || latest.backend_stage.toUpperCase()!=="VERIFYING"
                    || latest.target_project_ref!==projectRef
                    || Number(latest.progress_percent)<70
                    || latest.action_kind) {
                    setNotice("Project stage or identity changed. Refresh the Digital Display before retrying registration.");
                    void loadNewInfrastructureLive(true);
                    return;
                  }
                  void selectNewInfrastructureProject(setupId,projectRef);
                }}
                onVerify={() => void verifyAccess()}
                onRefresh={() => {
                  void refreshSession();
                  void loadNewInfrastructureLive(true);
                }}
                onClose={() => {
                  resetSession();
                  setMode("infrastructure");
                  void loadNewInfrastructureLive(true);
                }}
              />
            )}

            {session && String(session.mode).toUpperCase() !== "SETUP" && activeCapability && (
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-foreground/8 bg-foreground/[0.02] p-3.5">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary"><InfrastructureIcon serviceType={activeCapability.service_type} /></span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{activeCapability.display_name}</p>
                    <p className="mt-0.5 text-[10px] text-foreground/42">{activeCapability.provider_key} · {pretty(activeCapability.service_type)} · {activeCapability.logical_capability_hint || activeProfile?.logical_capability}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge className="bg-foreground/[0.06] text-foreground/55">{session.status}</Badge>
                  <Badge className="bg-primary/[0.08] text-primary">{activeCapability.execution?.coverage_state || activeCapability.state}</Badge>
                </div>
              </div>
            )}

            {session && String(session.mode).toUpperCase() !== "SETUP" && step === "WHERE" && catalogue && (
              <TargetStep catalogue={catalogue} busy={busy} preference={targetPreference} onSelect={chooseTarget} />
            )}

            {session && String(session.mode).toUpperCase() !== "SETUP" && step === "REUSE_OR_CREATE" && (
              <ReuseStep candidates={resolved?.reuse_candidates ?? []} busy={busy} onChoose={chooseReuse} />
            )}

            {session && String(session.mode).toUpperCase() !== "SETUP" && step === "ACCESS_REQUIREMENTS" && (
              <AccessStep requirements={resolved?.requirements ?? []} busy={busy} onAction={requirementAction} onBack={() => void patchSession({configuration:mergedConfiguration({acquisition_choice:""})})} />
            )}

            {session && String(session.mode).toUpperCase() !== "SETUP" && step === "CONFIGURE" && activeProfile && (
              <ConfigureStep
                profile={activeProfile}
                session={session}
                values={configuration}
                busy={busy}
                onChange={(key,value) => setConfiguration((current) => ({...current,[key]:value}))}
                onEnvironment={(value) => void patchSession({environment_key:value})}
                onRole={(value) => void patchSession({assignment_role:value})}
                onBack={() => void patchSession({configuration:mergedConfiguration({acquisition_choice:""})})}
                onReview={reviewPlan}
              />
            )}

            {session && String(session.mode).toUpperCase() !== "SETUP" && step === "REVIEW" && (
              <ReviewStep
                resolved={resolved}
                role={role}
                busy={busy}
                onBack={() => void patchSession({current_step:"CONFIGURE"})}
                onSaveProject={saveProjectPlan}
                onApprove={approveAndProvision}
              />
            )}

            {session && String(session.mode).toUpperCase() !== "SETUP" && step === "PROVISION" && (
              <ProvisionStep
                session={session}
                busy={busy}
                onRefresh={refreshSession}
                onOperations={() => onNavigate("flow")}
                onDone={returnToLauncher}
              />
            )}
          </div>
        </section>

        {mode !== "infrastructure" && (
          <div className="grid gap-3 md:grid-cols-3">
            <Boundary icon={KeyRound} title="Secrets stay backend-side" text="Tokens, keys and service credentials are submitted through protected activation and are not returned to the composer." />
            <Boundary icon={Fingerprint} title="Human authorization stays human" text="Provider login, MFA and consent open in the secure system browser and resume this exact RUNYI session." />
            <Boundary icon={Workflow} title="Flow remains executor" text="The Add Infrastructure interface plans and authorizes. Existing governed Flow/provider adapters perform provider mutation and verification." />
          </div>
        )}
      </div>

      {credentialOpen && credentialRequirement && String(session?.mode ?? "").toUpperCase() !== "SETUP" && (
        <CredentialSheet
          requirement={credentialRequirement}
          profile={activeProfile}
          values={credentialValues}
          busy={busy}
          onChange={(key,value) => setCredentialValues((current) => ({...current,[key]:value}))}
          onClose={() => {setCredentialOpen(false);setCredentialValues({});setCredentialRequirement(null);}}
          onSubmit={installCredential}
        />
      )}
    </div>
  );
}

function CatalogueChooser({
  catalogue,capabilities,categories,query,category,busy,mode,supabase,connected,onQuery,onCategory,onSelect,onRefresh,
}: {
  catalogue: Catalogue | null;
  capabilities: Capability[];
  categories: Array<[string,number]>;
  query: string;
  category: string;
  busy: boolean;
  mode: CreationMode;
  supabase: SupabaseClient | null;
  connected: boolean;
  onQuery: (value:string) => void;
  onCategory: (value:string) => void;
  onSelect: (item:Capability) => void;
  onRefresh: () => void;
}) {
  return (
    <div>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/30" />
          <Input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Search PostgreSQL, storage, queue, hosting, provider, capability…" className="h-11 rounded-xl border-foreground/10 bg-background pl-9 text-[16px]" />
        </div>
        <Button type="button" variant="outline" size="icon" className="size-11 shrink-0 rounded-xl" onClick={onRefresh} disabled={busy}><RefreshCw className={`size-4 ${busy?"animate-spin":""}`} /></Button>
      </div>

      <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
        <button type="button" onClick={() => onCategory("ALL")} className={`min-w-max rounded-lg border px-2.5 py-1.5 text-[9px] font-semibold ${category==="ALL"?"border-primary/25 bg-primary/[0.08] text-primary":"border-foreground/8 text-foreground/45"}`}>All · {catalogue?.capabilities.length ?? 0}</button>
        {categories.map(([key,count]) => <button key={key} type="button" onClick={() => onCategory(key)} className={`min-w-max rounded-lg border px-2.5 py-1.5 text-[9px] font-semibold ${category===key?"border-primary/25 bg-primary/[0.08] text-primary":"border-foreground/8 text-foreground/45"}`}>{pretty(key)} · {count}</button>)}
      </div>

      {!catalogue && busy ? (
        <div className="grid min-h-52 place-items-center"><LoaderCircle className="size-6 animate-spin text-primary" /></div>
      ) : (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {capabilities.map((item) => (
            <div key={item.provider_capability_key} className="flex items-start gap-1 rounded-2xl border border-foreground/8 bg-background/55 p-1.5 transition hover:border-foreground/15 hover:bg-foreground/[0.035]">
              <button type="button" onClick={() => onSelect(item)} disabled={busy} className="group flex min-w-0 flex-1 items-start gap-3 rounded-xl p-2 text-left disabled:opacity-50">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-foreground/[0.045] text-primary"><InfrastructureIcon serviceType={item.service_type} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{item.display_name}</span>
                  <span className="mt-1 block text-[10px] text-foreground/42">{item.provider_key} · {pretty(item.category_key)}</span>
                  <span className="mt-2 flex flex-wrap gap-1">
                    <Badge className="bg-foreground/[0.05] text-[9px] text-foreground/52">{item.execution?.coverage_state || item.state}</Badge>
                    {(item.provider_connections ?? []).some((connection) => connection.health_state === "HEALTHY") && <Badge className="bg-emerald-500/[0.07] text-[9px] text-emerald-300">CONNECTED</Badge>}
                    {item.approval_required && <Badge className="bg-amber-500/[0.07] text-[9px] text-amber-200">APPROVAL</Badge>}
                  </span>
                </span>
                <ChevronRight className="mt-1 size-4 shrink-0 text-foreground/20 transition group-hover:translate-x-0.5" />
              </button>
              <UniversalInfrastructureActions
                supabase={supabase}
                connected={connected}
                target={{
                  target_entity_key:`PROVIDER_CAPABILITY:${item.provider_capability_key}`,
                  display_name:item.display_name,
                  category_key:item.category_key,
                  provider_key:item.provider_key,
                  logical_capability:item.logical_capability_hint ?? item.requirement_profile?.logical_capability ?? null,
                  target_kind:"CAPABILITY",
                  metadata:{provider_capability_key:item.provider_capability_key},
                }}
                applications={(catalogue?.applications ?? []).map((app) => ({entity_key:app.entity_key,display_name:app.display_name}))}
                assignments={[]}
              />
            </div>
          ))}
          {capabilities.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-foreground/12 p-8 text-center text-xs text-foreground/40">No infrastructure capability matches this search.</div>}
        </div>
      )}
      <p className="mt-4 text-[10px] leading-5 text-foreground/35">{mode === "existing" ? "Add Existing discovers and adopts provider resources through the same access and verification contract." : "Capability availability never implies executable provisioning. RUNYI shows real execution readiness and blocks unsupported paths."}</p>
    </div>
  );
}

function TargetStep({catalogue,busy,preference,onSelect}: {
  catalogue: Catalogue;
  busy: boolean;
  preference:"APPLICATION" | "PROJECT" | null;
  onSelect:(kind:"application"|"project",id:string,environment?:string|null)=>void;
}) {
  return (
    <div>
      <StepHeading
        title={preference === "APPLICATION" ? "Add Infrastructure to App" : preference === "PROJECT" ? "Add Infrastructure to Project" : "Where should this infrastructure be used?"}
        text={preference === "APPLICATION" ? "Choose the application that should consume this registered infrastructure." : preference === "PROJECT" ? "Choose the Infrastructure Project that should receive this registered infrastructure as desired state." : "Choose an application or an Infrastructure Project. RUNYI pre-fills the environment and preserves project revision authority."}
      />
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {preference !== "PROJECT" && <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-foreground/35">Applications</p>
          <div className="space-y-2">
            {catalogue.applications.map((item) => <Choice key={item.entity_key} icon={AppWindow} title={item.display_name} meta={`${item.health_state ?? "UNKNOWN"} · ${item.environment_key ?? "Environment selected next"}`} disabled={busy} onClick={() => onSelect("application",item.entity_key,item.environment_key)} />)}
          </div>
        </div>}
        {preference !== "APPLICATION" && <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-foreground/35">Infrastructure Projects</p>
          <div className="space-y-2">
            {catalogue.projects.map((item) => <Choice key={item.project_id} icon={Boxes} title={item.display_name} meta={`${item.lifecycle_state} · ${item.environment_key}`} disabled={busy} onClick={() => onSelect("project",item.project_id,item.environment_key)} />)}
            {catalogue.projects.length === 0 && <div className="rounded-xl border border-dashed border-foreground/10 p-4 text-[10px] text-foreground/38">No Infrastructure Projects yet. Choose an application and RUNYI can create a linked project when the plan is saved.</div>}
          </div>
        </div>}
      </div>
    </div>
  );
}

function ReuseStep({candidates,busy,onChoose}: {
  candidates: ResolvedSession["reuse_candidates"];
  busy:boolean;
  onChoose:(resourceKey?:string)=>void;
}) {
  return (
    <div>
      <StepHeading title="Reuse existing or create dedicated?" text="RUNYI searches current inventory before creating duplicate infrastructure." />
      <div className="mt-4 space-y-2">
        {candidates.map((item) => <Choice key={item.resource_key} icon={Database} title={item.display_name} meta={`${item.provider_key ?? "Provider unknown"} · ${item.health_state} · ${item.environment_key ?? "Any environment"}`} disabled={busy} action="Use existing" onClick={() => onChoose(item.resource_key)} />)}
        {candidates.length === 0 && <div className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-4"><p className="text-sm font-semibold">No compatible reusable resource found</p><p className="mt-1 text-[10px] leading-5 text-foreground/42">RUNYI can continue with the selected provider capability if its access and execution path are ready.</p></div>}
        <Choice icon={Plus} title="Create dedicated infrastructure" meta="Keep this resource dedicated to the selected scope." disabled={busy} action="Create new" onClick={() => onChoose()} />
      </div>
    </div>
  );
}

function AccessStep({requirements,busy,onAction,onBack}: {
  requirements:Requirement[];
  busy:boolean;
  onAction:(requirement:Requirement)=>void;
  onBack:()=>void;
}) {
  const allReady = requirements.every((item) => !item.required || item.state === "SATISFIED");
  return (
    <div>
      <StepHeading title="Access requirements" text="Login, OAuth, tokens, secrets, MCP/app permissions and provider scopes are resolved here. Connected does not mean unrestricted." />
      <div className="mt-4 space-y-2">
        {requirements.map((item) => (
          <div key={item.requirement_key} className={`rounded-2xl border p-3.5 ${statusTone[item.state] ?? statusTone.OPTIONAL}`}>
            <div className="flex items-start gap-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-background/50">{item.state === "SATISFIED" ? <Check className="size-4" /> : item.state === "BLOCKED" ? <CircleAlert className="size-4" /> : <KeyRound className="size-4" />}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold">{item.label}</p>
                <p className="mt-0.5 text-[9px] uppercase tracking-wider opacity-70">{item.state} · {pretty(item.requirement_type)}</p>
                {Array.isArray(item.action_payload?.missing_capabilities) && item.action_payload.missing_capabilities.length > 0 && <p className="mt-2 text-[10px] leading-4 opacity-80">Missing: {item.action_payload.missing_capabilities.join(", ")}</p>}
              </div>
              {item.state !== "SATISFIED" && item.action_kind && (
                <Button type="button" size="sm" variant="outline" disabled={busy || item.state === "BLOCKED"} onClick={() => onAction(item)} className="min-h-9 shrink-0 rounded-xl text-[10px]">
                  {actionLabel(item.action_kind)}
                  {item.action_kind === "BROWSER_AUTHORIZATION" && <ExternalLink className="size-3.5" />}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <Button type="button" variant="outline" className="rounded-xl" onClick={onBack} disabled={busy}><ChevronLeft className="size-4" />Reuse/Create</Button>
        {allReady && <Badge className="bg-emerald-500/[0.08] text-emerald-300">ALL ACCESS READY</Badge>}
      </div>
    </div>
  );
}

function ConfigureStep({profile,session,values,busy,onChange,onEnvironment,onRole,onBack,onReview}: {
  profile:RequirementProfile;
  session:AddSession;
  values:Record<string,string>;
  busy:boolean;
  onChange:(key:string,value:string)=>void;
  onEnvironment:(value:string)=>void;
  onRole:(value:string)=>void;
  onBack:()=>void;
  onReview:()=>void;
}) {
  const fields = profile.configuration_schema?.fields ?? [];
  return (
    <div>
      <StepHeading title="Configure infrastructure" text="RUNYI renders this form from the backend capability profile. Raw provider configuration stays behind Advanced controls." />
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field label="Environment">
          <select value={session.environment_key} onChange={(event) => onEnvironment(event.target.value)} className="h-11 w-full rounded-xl border border-foreground/10 bg-background px-3 text-[13px] outline-none">
            {["DEV","STAGING","CANARY","PRODUCTION"].map((value) => <option key={value} value={value}>{pretty(value)}</option>)}
          </select>
        </Field>
        <Field label="Assignment role">
          <select value={session.assignment_role} onChange={(event) => onRole(event.target.value)} className="h-11 w-full rounded-xl border border-foreground/10 bg-background px-3 text-[13px] outline-none">
            {["PRIMARY","STANDBY","BACKUP","ANALYTICS","CACHE","ARCHIVE"].map((value) => <option key={value} value={value}>{pretty(value)}</option>)}
          </select>
        </Field>
        {fields.map((field) => (
          <Field key={field.key} label={field.label}>
            <Input value={values[field.key] ?? String(field.default ?? "")} onChange={(event) => onChange(field.key,event.target.value)} placeholder={field.required ? "Required" : "Automatic / optional"} className="h-11 rounded-xl border-foreground/10 bg-background text-[16px]" />
          </Field>
        ))}
      </div>
      <div className="mt-5 flex justify-between gap-2">
        <Button type="button" variant="outline" className="rounded-xl" onClick={onBack} disabled={busy}><ChevronLeft className="size-4" />Reuse/Create</Button>
        <Button type="button" className="min-h-11 rounded-xl" onClick={onReview} disabled={busy}>{busy?<LoaderCircle className="size-4 animate-spin" />:<ShieldCheck className="size-4" />}Review Infrastructure</Button>
      </div>
    </div>
  );
}

function ReviewStep({resolved,role,busy,onBack,onSaveProject,onApprove}: {
  resolved:ResolvedSession | null;
  role?:string;
  busy:boolean;
  onBack:()=>void;
  onSaveProject:()=>void;
  onApprove:()=>void;
}) {
  const session = resolved?.session;
  const capability = resolved?.capability;
  const preview = resolved?.preview ?? session?.result?.preview ?? session?.result?.inputs?.preview;
  const resolution = preview?.resolution ?? session?.result?.preview?.resolution ?? [];
  const projectOnly = Boolean(session?.project_id && !session?.application_entity_key);
  return (
    <div>
      <StepHeading title="Review infrastructure plan" text="This is the last planning boundary before governed publication/materialization. Provider mutation still occurs only through existing Flow/provider adapters." />
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <Summary label="Infrastructure" value={capability?.display_name ?? session?.provider_capability_key ?? "Unknown"} />
        <Summary label="Provider" value={session?.provider_key ?? "Automatic"} />
        <Summary label="Target" value={session?.application_entity_key ?? session?.project_id ?? "Unassigned"} />
        <Summary label="Environment / role" value={`${session?.environment_key ?? "PRODUCTION"} · ${session?.assignment_role ?? "PRIMARY"}`} />
        <Summary label="Reuse policy" value={pretty(session?.reuse_policy)} />
        <Summary label="Plan readiness" value={preview?.readiness ?? session?.status ?? "PLAN READY"} />
      </div>
      {Array.isArray(resolution) && resolution.length > 0 && (
        <div className="mt-3 rounded-2xl border border-foreground/8 bg-foreground/[0.02] p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-foreground/35">Resolution</p>
          {resolution.map((item:any,index:number) => <div key={index} className="mt-2 flex items-center justify-between gap-3 text-[10px]"><span>{item.slot}</span><span className="text-foreground/48">{item.status} · {item.provider_key || "Provider unresolved"}</span></div>)}
        </div>
      )}
      <div className="mt-5 flex flex-wrap justify-between gap-2">
        <Button type="button" variant="outline" className="rounded-xl" onClick={onBack} disabled={busy}><ChevronLeft className="size-4" />Configure</Button>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" className="rounded-xl" onClick={onSaveProject} disabled={busy}><Boxes className="size-4" />Save Project Plan</Button>
          {!projectOnly && (
            <Button type="button" className="min-h-11 rounded-xl" onClick={onApprove} disabled={busy || role !== "OWNER_ADMIN"}>
              {busy?<LoaderCircle className="size-4 animate-spin" />:<ShieldCheck className="size-4" />}
              {role === "OWNER_ADMIN" ? "Approve & Provision" : "Owner Approval Required"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProvisionStep({session,busy,onRefresh,onOperations,onDone}: {
  session:AddSession;
  busy:boolean;
  onRefresh:()=>void;
  onOperations:()=>void;
  onDone:()=>void;
}) {
  const active = session.status === "ACTIVE";
  const degraded = session.status === "DEGRADED";
  const assignments = Array.isArray(session.result?.assignment_states) ? session.result?.assignment_states : [];
  return (
    <div>
      <StepHeading title={active ? "Infrastructure active" : degraded ? "Infrastructure needs attention" : "Governed provisioning"} text={active ? "Provider truth and assignment state have reached ACTIVE." : "The composition is materialized. RUNYI keeps the acquisition session open while existing Flow/provider execution and verification progress."} />
      <div className="mt-4 rounded-2xl border border-foreground/8 bg-foreground/[0.02] p-4">
        <div className="flex items-center gap-3">
          <span className={`grid size-11 place-items-center rounded-2xl ${active?"bg-emerald-500/[0.08] text-emerald-300":degraded?"bg-red-500/[0.08] text-red-300":"bg-primary/[0.08] text-primary"}`}>{active?<Check className="size-5" />:<Workflow className="size-5" />}</span>
          <div><p className="text-sm font-semibold">{session.status}</p><p className="mt-1 text-[10px] text-foreground/42">Session {session.session_id}</p></div>
        </div>
        {assignments.map((item:any) => <div key={item.assignment_id} className="mt-3 flex items-center justify-between rounded-xl border border-foreground/8 bg-background p-3 text-[10px]"><span className="truncate">{item.target_entity_key}</span><Badge className="bg-foreground/[0.06] text-foreground/55">{item.state}</Badge></div>)}
      </div>
      <div className="mt-5 flex flex-wrap justify-between gap-2">
        <Button type="button" variant="outline" className="rounded-xl" onClick={onRefresh} disabled={busy}>{busy?<LoaderCircle className="size-4 animate-spin" />:<RefreshCw className="size-4" />}Refresh Status</Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="rounded-xl" onClick={onOperations}><Workflow className="size-4" />Operations</Button>
          {active && <Button type="button" className="rounded-xl" onClick={onDone}>Add Another</Button>}
        </div>
      </div>
    </div>
  );
}

function CredentialSheet({requirement,profile,values,busy,onChange,onClose,onSubmit}: {
  requirement:Requirement;
  profile:RequirementProfile | null | undefined;
  values:Record<string,string>;
  busy:boolean;
  onChange:(key:string,value:string)=>void;
  onClose:()=>void;
  onSubmit:()=>void;
}) {
  const fields: CredentialField[] = requirement.action_payload?.credential_schema?.fields
    ?? profile?.credential_schema?.fields
    ?? [];
  return (
    <div className="fixed inset-0 z-[180] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={(event) => {if(event.target===event.currentTarget&&!busy) onClose();}}>
      <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-[26px] border border-foreground/10 bg-background shadow-2xl sm:rounded-[26px]">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-foreground/8 bg-background/95 p-4 backdrop-blur-xl">
          <div><p className="text-[9px] font-bold uppercase tracking-[0.18em] text-primary">Secure credential activation</p><h2 className="mt-1 text-lg font-semibold">{requirement.label}</h2><p className="mt-1 text-[10px] leading-5 text-foreground/42">Values are submitted directly to the protected backend activation path and are cleared from this form after submission.</p></div>
          <button type="button" onClick={onClose} disabled={busy} className="grid size-9 shrink-0 place-items-center rounded-xl border border-foreground/8"><X className="size-4" /></button>
        </div>
        <div className="space-y-3 p-4">
          {fields.length === 0 && <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-3 text-[10px] leading-5 text-amber-100">This provider has no secure credential input schema registered yet. RUNYI will not request arbitrary secrets.</div>}
          {fields.map((field) => (
            <Field key={field.key} label={`${field.label}${field.required?" *":""}`}>
              {field.type === "secret_textarea" ? (
                <Textarea value={values[field.key] ?? ""} onChange={(event) => onChange(field.key,event.target.value)} className="min-h-32 rounded-xl border-foreground/10 bg-card font-mono text-[13px]" autoComplete="off" spellCheck={false} />
              ) : (
                <Input type={field.type === "secret" ? "password" : "text"} value={values[field.key] ?? ""} onChange={(event) => onChange(field.key,event.target.value)} className="h-11 rounded-xl border-foreground/10 bg-card text-[16px]" autoComplete="off" spellCheck={false} />
              )}
            </Field>
          ))}
          <Button type="button" className="min-h-11 w-full rounded-xl" onClick={onSubmit} disabled={busy || fields.length === 0}>{busy?<LoaderCircle className="size-4 animate-spin" />:<KeyRound className="size-4" />}Save Securely & Verify</Button>
          <p className="text-center text-[9px] leading-4 text-foreground/30">RUNYI does not display the credential again after successful installation.</p>
        </div>
      </div>
    </div>
  );
}

function InfrastructureIcon({serviceType}:{serviceType:string}) {
  const Icon = categoryIcons[String(serviceType).toUpperCase()] ?? Database;
  return <Icon className="size-4.5" />;
}

function StepHeading({title,text}:{title:string;text:string}) {
  return <div><p className="text-[9px] font-bold uppercase tracking-[0.18em] text-primary">Universal Add Infrastructure</p><h2 className="mt-1 text-xl font-semibold tracking-tight">{title}</h2><p className="mt-1 max-w-2xl text-[11px] leading-5 text-foreground/45">{text}</p></div>;
}

function Choice({icon:Icon,title,meta,action="Select",disabled,onClick}:{icon:LucideIcon;title:string;meta:string;action?:string;disabled?:boolean;onClick:()=>void}) {
  return <button type="button" disabled={disabled} onClick={onClick} className="flex w-full items-center gap-3 rounded-2xl border border-foreground/8 bg-background p-3 text-left transition hover:bg-foreground/[0.035] disabled:opacity-50"><span className="grid size-9 shrink-0 place-items-center rounded-xl bg-foreground/[0.05] text-primary"><Icon className="size-4" /></span><span className="min-w-0 flex-1"><span className="block truncate text-[11px] font-semibold">{title}</span><span className="mt-0.5 block truncate text-[9px] text-foreground/38">{meta}</span></span><span className="text-[9px] font-semibold text-primary">{action}</span><ChevronRight className="size-3.5 text-foreground/22" /></button>;
}

function Field({label,children}:{label:string;children:ReactNode}) {
  return <label className="block"><span className="mb-1.5 block text-[10px] font-semibold text-foreground/52">{label}</span>{children}</label>;
}

function Summary({label,value}:{label:string;value:string}) {
  return <div className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3"><p className="text-[9px] uppercase tracking-wider text-foreground/30">{label}</p><p className="mt-1 break-words text-[11px] font-semibold">{value}</p></div>;
}

function Boundary({icon:Icon,title,text}:{icon:LucideIcon;title:string;text:string}) {
  return <Card className="border-foreground/8 bg-card py-0"><CardContent className="p-4"><span className="grid size-9 place-items-center rounded-xl bg-primary/[0.08] text-primary"><Icon className="size-4" /></span><p className="mt-3 text-[11px] font-semibold">{title}</p><p className="mt-1 text-[9px] leading-4 text-foreground/38">{text}</p></CardContent></Card>;
}

function actionLabel(action:string) {
  if (action === "BROWSER_AUTHORIZATION") return "Authorize";
  if (action === "ADD_API_TOKEN" || action === "ADD_TOKEN") return "Add Token";
  if (action === "ADD_SERVICE_ACCOUNT") return "Add Service Account";
  if (action === "ADD_GITHUB_APP") return "Connect GitHub App";
  if (action === "SELECT_TARGET") return "Choose Target";
  if (action === "VERIFY") return "Verify";
  if (action === "VIEW_EXECUTION_READINESS") return "Not Ready";
  return "Resolve";
}
